(() => {
  "use strict";

  /* =========================================================
     BNX / SHOPIFY PRODUCT BUILDER
     MODULAR PURCHASE-OPTION SYSTEM
     ========================================================= */

  const SELECTORS = {
    builder: "[data-bnx-product-builder]",

    size: "[data-bnx-size]",
    sizePrice: "[data-bnx-size-price]",

    quantity: "[data-bnx-quantity]",
    quantityImages: "[data-bnx-quantity-images]",
    quantityDescription: "[data-bnx-quantity-description]",

    totalPrice: "[data-bnx-total-price]",
    comparePrice: "[data-bnx-compare-price]",
    eachPrice: "[data-bnx-each-price]",
    discountText: "[data-bnx-discount-text]",

    addButton: "[data-bnx-add-to-cart]",
    buttonText: "[data-bnx-button-text]",
    message: "[data-bnx-message]",

    decrease: "[data-bnx-decrease]",
    increase: "[data-bnx-increase]",
    counterValue: "[data-bnx-counter-value]"
  };

  const initializedBuilders = new WeakSet();

  /* =========================================================
     HELPERS
     ========================================================= */

  function parseBoolean(value) {
    return value === true || value === "true";
  }

  function parseInteger(value, fallback = 0) {
    const parsed = Number.parseInt(value, 10);

    return Number.isFinite(parsed)
      ? parsed
      : fallback;
  }

  function parseNumber(value, fallback = 0) {
    const parsed = Number(value);

    return Number.isFinite(parsed)
      ? parsed
      : fallback;
  }

  function clamp(value, minimum, maximum) {
    return Math.min(
      maximum,
      Math.max(minimum, value)
    );
  }

  function normalizeString(value) {
    return String(value || "")
      .trim()
      .toLowerCase();
  }

  function parseList(value) {
    if (
      value === undefined ||
      value === null ||
      value === ""
    ) {
      return [];
    }

    return String(value)
      .split(/[|,;]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  function formatMoney(cents) {
    const amount =
      Math.max(0, Number(cents) || 0) / 100;

    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || "en-US",
        {
          style: "currency",
          currency:
            window.Shopify?.currency?.active ||
            "USD"
        }
      ).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  /* =========================================================
     PRODUCT BUILDER
     ========================================================= */

  class ShopifyProductBuilder {
    constructor(element) {
      this.element = element;

      this.sizeOptions = Array.from(
        element.querySelectorAll(
          SELECTORS.size
        )
      );

      this.quantityOptions = Array.from(
        element.querySelectorAll(
          SELECTORS.quantity
        )
      );

      this.addButton =
        element.querySelector(
          SELECTORS.addButton
        );

      this.buttonText =
        element.querySelector(
          SELECTORS.buttonText
        );

      this.message =
        element.querySelector(
          SELECTORS.message
        );

      this.decreaseButton =
        element.querySelector(
          SELECTORS.decrease
        );

      this.increaseButton =
        element.querySelector(
          SELECTORS.increase
        );

      this.counterValue =
        element.querySelector(
          SELECTORS.counterValue
        );

      this.selectedSize = null;
      this.selectedQuantity = null;

      this.visibleQuantityOptions = [];

      this.isAdding = false;

      this.handleAddToCart =
        this.handleAddToCart.bind(this);
    }

    /* =======================================================
       INITIALIZATION
       ======================================================= */

    init() {
      if (
        this.sizeOptions.length === 0 ||
        this.quantityOptions.length === 0
      ) {
        this.disableAddButton();
        return;
      }

      this.prepareShopifyPrices();
      this.prepareQuantityOptions();
      this.bindEvents();

      const requestedVariantId =
        new URLSearchParams(
          window.location.search
        ).get("variant");

      const initialSize =
        (
          requestedVariantId
            ? this.sizeOptions.find(
                (option) =>
                  !option.disabled &&
                  String(
                    option.dataset.variantId
                  ) ===
                    String(requestedVariantId)
              )
            : null
        ) ||
        this.sizeOptions.find(
          (option) =>
            !option.disabled &&
            parseBoolean(
              option.dataset.defaultSelected
            )
        ) ||
        this.sizeOptions.find(
          (option) =>
            !option.disabled &&
            option.classList.contains(
              "is-selected"
            )
        ) ||
        this.sizeOptions.find(
          (option) =>
            !option.disabled
        );

      if (initialSize) {
        this.selectSize(
          initialSize,
          false
        );
      } else {
        this.refreshQuantityAvailability();
      }

      this.updateInterface();
    }

    /* =======================================================
       SHOPIFY PRICE PREPARATION
       ======================================================= */

    prepareShopifyPrices() {
      this.sizeOptions.forEach(
        (option) => {
          const priceCents =
            parseInteger(
              option.dataset.priceCents,
              -1
            );

          const variantId =
            parseInteger(
              option.dataset.variantId,
              0
            );

          const available =
            parseBoolean(
              option.dataset.available
            );

          const priceElement =
            option.querySelector(
              SELECTORS.sizePrice
            );

          const priceReady =
            variantId > 0 &&
            priceCents >= 0;

          option.dataset.shopifyPriceReady =
            String(priceReady);

          if (
            priceReady &&
            available
          ) {
            option.disabled = false;

            option.classList.remove(
              "is-price-unavailable"
            );

            if (priceElement) {
              priceElement.textContent =
                formatMoney(priceCents);
            }
          } else {
            option.classList.add(
              "is-price-unavailable"
            );

            if (!priceReady) {
              option.disabled = true;

              if (priceElement) {
                priceElement.textContent =
                  "Price unavailable";
              }
            }
          }
        }
      );
    }

    /* =======================================================
       MODULAR PURCHASE OPTION PREPARATION
       ======================================================= */

    prepareQuantityOptions() {
      this.quantityOptions.forEach(
        (option, index) => {
          option.dataset.bnxOriginalIndex =
            String(index);

          option.hidden = false;

          option.classList.remove(
            "is-bnx-hidden"
          );

          option.setAttribute(
            "aria-hidden",
            "false"
          );
        }
      );
    }

    /* =======================================================
       MODULAR PURCHASE OPTION MATCHING
       ======================================================= */

    quantityOptionMatchesSelectedSize(
      quantityOption
    ) {
      if (
        !quantityOption ||
        !this.selectedSize
      ) {
        return false;
      }

      /*
       * SUPPORTED CONFIGURATION ATTRIBUTES
       *
       * The Liquid can assign purchase options using ANY of:
       *
       * data-bnx-variant-ids
       * data-variant-ids
       * data-bnx-variant-id
       * data-variant-id
       *
       * data-bnx-size-names
       * data-size-names
       * data-bnx-size-name
       * data-size-name
       *
       * data-bnx-product-handles
       * data-product-handles
       * data-bnx-product-handle
       * data-product-handle
       *
       * If none are present, the purchase option is treated
       * as GLOBAL and is available for every size.
       */

      const selectedVariantId =
        String(
          this.selectedSize.dataset.variantId ||
          ""
        ).trim();

      const selectedSizeName =
        normalizeString(
          this.selectedSize.dataset.sizeName
        );

      const selectedProductHandle =
        normalizeString(
          this.selectedSize.dataset.productHandle
        );

      const variantValues = [
        ...parseList(
          quantityOption.dataset.bnxVariantIds
        ),
        ...parseList(
          quantityOption.dataset.variantIds
        ),
        ...parseList(
          quantityOption.dataset.bnxVariantId
        ),
        ...parseList(
          quantityOption.dataset.variantId
        )
      ].map(String);

      const sizeValues = [
        ...parseList(
          quantityOption.dataset.bnxSizeNames
        ),
        ...parseList(
          quantityOption.dataset.sizeNames
        ),
        ...parseList(
          quantityOption.dataset.bnxSizeName
        ),
        ...parseList(
          quantityOption.dataset.sizeName
        )
      ].map(normalizeString);

      const productHandleValues = [
        ...parseList(
          quantityOption.dataset.bnxProductHandles
        ),
        ...parseList(
          quantityOption.dataset.productHandles
        ),
        ...parseList(
          quantityOption.dataset.bnxProductHandle
        ),
        ...parseList(
          quantityOption.dataset.productHandle
        )
      ].map(normalizeString);

      const hasVariantRestriction =
        variantValues.length > 0;

      const hasSizeRestriction =
        sizeValues.length > 0;

      const hasProductRestriction =
        productHandleValues.length > 0;

      const hasAnyRestriction =
        hasVariantRestriction ||
        hasSizeRestriction ||
        hasProductRestriction;

      /*
       * No assignment = global purchase option.
       */

      if (!hasAnyRestriction) {
        return true;
      }

      /*
       * Variant ID is the strongest and safest match.
       */

      if (
        hasVariantRestriction &&
        selectedVariantId &&
        variantValues.includes(
          selectedVariantId
        )
      ) {
        return true;
      }

      /*
       * Size-name matching is supported for flexible
       * Theme Editor configuration.
       */

      if (
        hasSizeRestriction &&
        selectedSizeName &&
        sizeValues.includes(
          selectedSizeName
        )
      ) {
        return true;
      }

      /*
       * Product-handle matching is also supported.
       */

      if (
        hasProductRestriction &&
        selectedProductHandle &&
        productHandleValues.includes(
          selectedProductHandle
        )
      ) {
        return true;
      }

      return false;
    }

    /* =======================================================
       REFRESH PURCHASE OPTIONS FOR SELECTED VARIANT
       ======================================================= */

    refreshQuantityAvailability() {
      this.visibleQuantityOptions = [];

      this.quantityOptions.forEach(
        (option) => {
          const shouldShow =
            this.quantityOptionMatchesSelectedSize(
              option
            );

          option.hidden = !shouldShow;

          option.classList.toggle(
            "is-bnx-hidden",
            !shouldShow
          );

          option.setAttribute(
            "aria-hidden",
            String(!shouldShow)
          );

          if (shouldShow) {
            this.visibleQuantityOptions.push(
              option
            );
          } else {
            option.classList.remove(
              "is-selected"
            );

            option.setAttribute(
              "aria-checked",
              "false"
            );
          }
        }
      );

      this.ensureValidQuantitySelection();
    }

    /* =======================================================
       ENSURE CURRENT PURCHASE OPTION IS VALID
       ======================================================= */

    ensureValidQuantitySelection() {
      if (
        this.visibleQuantityOptions.length === 0
      ) {
        this.selectedQuantity = null;

        this.quantityOptions.forEach(
          (option) => {
            option.classList.remove(
              "is-selected"
            );

            option.setAttribute(
              "aria-checked",
              "false"
            );
          }
        );

        this.updateCounter();
        this.updateAddButtonState();

        return;
      }

      const currentSelectionStillValid =
        this.selectedQuantity &&
        this.visibleQuantityOptions.includes(
          this.selectedQuantity
        );

      if (currentSelectionStillValid) {
        return;
      }

      const defaultOption =
        this.visibleQuantityOptions.find(
          (option) =>
            parseBoolean(
              option.dataset.defaultSelected
            )
        ) ||
        this.visibleQuantityOptions.find(
          (option) =>
            option.classList.contains(
              "is-selected"
            )
        ) ||
        this.visibleQuantityOptions[0];

      this.selectQuantity(
        defaultOption,
        false
      );
    }

    /* =======================================================
       EVENTS
       ======================================================= */

    bindEvents() {
      this.sizeOptions.forEach(
        (option) => {
          option.addEventListener(
            "click",
            () => {
              if (
                option.disabled ||
                !parseBoolean(
                  option.dataset
                    .shopifyPriceReady
                )
              ) {
                return;
              }

              this.selectSize(option);
            }
          );
        }
      );

      this.quantityOptions.forEach(
        (option) => {
          option.addEventListener(
            "click",
            () => {
              if (
                option.hidden ||
                option.classList.contains(
                  "is-bnx-hidden"
                )
              ) {
                return;
              }

              this.selectQuantity(option);
            }
          );
        }
      );

      this.decreaseButton?.addEventListener(
        "click",
        () => {
          this.selectAdjacentQuantity(-1);
        }
      );

      this.increaseButton?.addEventListener(
        "click",
        () => {
          this.selectAdjacentQuantity(1);
        }
      );

      this.addButton?.addEventListener(
        "click",
        this.handleAddToCart
      );
    }

    /* =======================================================
       VARIANT / SIZE SELECTION
       ======================================================= */

    selectSize(
      option,
      shouldClearMessage = true
    ) {
      if (
        !option ||
        option.disabled ||
        !parseBoolean(
          option.dataset.shopifyPriceReady
        )
      ) {
        return;
      }

      this.selectedSize = option;

      this.sizeOptions.forEach(
        (sizeOption) => {
          const selected =
            sizeOption === option;

          sizeOption.classList.toggle(
            "is-selected",
            selected
          );

          sizeOption.setAttribute(
            "aria-checked",
            String(selected)
          );
        }
      );

      this.updateVariantUrl(option);

      /*
       * IMPORTANT:
       * Purchase-option visibility is recalculated
       * every time the Shopify variant changes.
       */

      this.refreshQuantityAvailability();

      this.updateInterface();

      if (shouldClearMessage) {
        this.clearMessage();
      }

      window.dispatchEvent(
        new CustomEvent(
          "bnx-product-builder:variant-changed",
          {
            detail: {
              variantId:
                option.dataset.variantId,

              sizeName:
                option.dataset.sizeName,

              priceCents:
                option.dataset.priceCents,

              visiblePurchaseOptions:
                this.visibleQuantityOptions.length
            }
          }
        )
      );
    }

    /* =======================================================
       VARIANT URL SYNCHRONIZATION
       ======================================================= */

    updateVariantUrl(option) {
      const variantId =
        option?.dataset.variantId;

      if (!variantId) {
        return;
      }

      try {
        const url = new URL(
          window.location.href
        );

        url.searchParams.set(
          "variant",
          variantId
        );

        /*
         * replaceState is intentional.
         *
         * DO NOT redirect or reload the product page.
         */

        window.history.replaceState(
          {},
          "",
          url.toString()
        );
      } catch (error) {
        // URL synchronization is optional.
      }
    }

    /* =======================================================
       PURCHASE OPTION SELECTION
       ======================================================= */

    selectQuantity(
      option,
      shouldClearMessage = true
    ) {
      if (
        !option ||
        option.hidden ||
        option.classList.contains(
          "is-bnx-hidden"
        )
      ) {
        return;
      }

      this.selectedQuantity = option;

      this.quantityOptions.forEach(
        (quantityOption) => {
          const selected =
            quantityOption === option;

          quantityOption.classList.toggle(
            "is-selected",
            selected
          );

          quantityOption.setAttribute(
            "aria-checked",
            String(selected)
          );
        }
      );

      this.updateCounter();
      this.updateAddButtonState();

      if (shouldClearMessage) {
        this.clearMessage();
      }

      window.dispatchEvent(
        new CustomEvent(
          "bnx-product-builder:purchase-option-changed",
          {
            detail: {
              quantity:
                parseInteger(
                  option.dataset.quantity,
                  1
                ),

              label:
                option.dataset.optionLabel ||
                option.dataset.label ||
                "",

              variantId:
                this.selectedSize?.dataset
                  .variantId ||
                ""
            }
          }
        )
      );
    }

    /* =======================================================
       ADJACENT PURCHASE OPTION
       ======================================================= */

    selectAdjacentQuantity(direction) {
      if (
        !this.selectedQuantity ||
        this.visibleQuantityOptions.length === 0
      ) {
        return;
      }

      const currentIndex =
        this.visibleQuantityOptions.indexOf(
          this.selectedQuantity
        );

      if (currentIndex < 0) {
        this.selectQuantity(
          this.visibleQuantityOptions[0]
        );

        return;
      }

      const nextIndex = clamp(
        currentIndex + direction,
        0,
        this.visibleQuantityOptions.length - 1
      );

      this.selectQuantity(
        this.visibleQuantityOptions[nextIndex]
      );
    }

    /* =======================================================
       INTERFACE
       ======================================================= */

    updateInterface() {
      if (!this.selectedSize) {
        this.clearCalculatedPrices();
        this.updateCounter();
        this.updateAddButtonState();

        return;
      }

      this.visibleQuantityOptions.forEach(
        (option) => {
          this.renderQuantityImages(
            option
          );

          this.updateQuantityPrice(
            option
          );

          this.updateQuantityDescription(
            option
          );
        }
      );

      this.updateCounter();
      this.updateAddButtonState();
    }

    /* =======================================================
       QUANTITY / PURCHASE OPTION IMAGES
       ======================================================= */

    renderQuantityImages(
      quantityOption
    ) {
      const container =
        quantityOption.querySelector(
          SELECTORS.quantityImages
        );

      if (
        !container ||
        !this.selectedSize
      ) {
        return;
      }

      const imageUrl =
        this.selectedSize.dataset.imageUrl;

      const sizeName =
        this.selectedSize.dataset.sizeName ||
        "Product";

      const quantity = Math.max(
        1,
        parseInteger(
          quantityOption.dataset.quantity,
          1
        )
      );

      container.innerHTML = "";

      const visibleImageCount =
        quantity <= 6
          ? quantity
          : 1;

      container.dataset.imageCount =
        String(visibleImageCount);

      container.classList.toggle(
        "is-collapsed",
        quantity > 6
      );

      if (!imageUrl) {
        return;
      }

      for (
        let index = 0;
        index < visibleImageCount;
        index += 1
      ) {
        const image =
          document.createElement("img");

        image.src = imageUrl;
        image.alt = "";
        image.loading = "lazy";

        image.className =
          "bnx-product-builder__quantity-image";

        container.appendChild(image);
      }

      if (quantity > 6) {
        const countBadge =
          document.createElement("span");

        countBadge.className =
          "bnx-product-builder__image-count-badge";

        countBadge.textContent =
          `×${quantity}`;

        countBadge.setAttribute(
          "aria-label",
          `${quantity} ${sizeName}`
        );

        container.appendChild(
          countBadge
        );
      }
    }

    /* =======================================================
       PURCHASE OPTION DESCRIPTION
       ======================================================= */

    updateQuantityDescription(
      quantityOption
    ) {
      const description =
        quantityOption.querySelector(
          SELECTORS.quantityDescription
        );

      if (
        !description ||
        !this.selectedSize
      ) {
        return;
      }

      /*
       * FULL FREEDOM:
       *
       * If Liquid provides a custom description,
       * JS uses it exactly.
       *
       * Otherwise we retain the old automatic
       * quantity × size fallback.
       */

      const customDescription =
        quantityOption.dataset
          .optionDescription ||
        quantityOption.dataset
          .description ||
        "";

      if (
        String(customDescription).trim() !== ""
      ) {
        description.textContent =
          customDescription;

        return;
      }

      const quantity = Math.max(
        1,
        parseInteger(
          quantityOption.dataset.quantity,
          1
        )
      );

      const sizeName =
        this.selectedSize.dataset.sizeName ||
        "Product";

      description.textContent =
        quantity === 1
          ? `1 × ${sizeName}`
          : `${quantity} × ${sizeName}`;
    }

    /* =======================================================
       DISPLAYED PURCHASE OPTION PRICING
       ======================================================= */

    calculatePricing(
      quantityOption
    ) {
      if (!this.selectedSize) {
        return null;
      }

      const priceReady =
        parseBoolean(
          this.selectedSize.dataset
            .shopifyPriceReady
        );

      if (!priceReady) {
        return null;
      }

      const unitPrice =
        parseInteger(
          this.selectedSize.dataset
            .priceCents,
          -1
        );

      if (unitPrice < 0) {
        return null;
      }

      const quantity = Math.max(
        1,
        parseInteger(
          quantityOption.dataset.quantity,
          1
        )
      );

      const originalTotal =
        unitPrice * quantity;

      const discountType =
        quantityOption.dataset.discountType ||
        "none";

      const discountValue =
        Math.max(
          0,
          parseNumber(
            quantityOption.dataset
              .discountValue,
            0
          )
        );

      let discountCents = 0;
      let discountLabel = "";

      switch (discountType) {
        case "percentage": {
          const percentage = clamp(
            discountValue,
            0,
            100
          );

          discountCents =
            Math.round(
              originalTotal *
                (percentage / 100)
            );

          if (percentage > 0) {
            discountLabel =
              `${percentage}% savings`;
          }

          break;
        }

        case "fixed_each": {
          const centsOffEach =
            Math.round(
              discountValue * 100
            );

          discountCents =
            centsOffEach * quantity;

          if (discountValue > 0) {
            discountLabel =
              `${formatMoney(
                centsOffEach
              )} off each`;
          }

          break;
        }

        case "fixed_total": {
          discountCents =
            Math.round(
              discountValue * 100
            );

          if (discountValue > 0) {
            discountLabel =
              `${formatMoney(
                discountCents
              )} savings`;
          }

          break;
        }

        default: {
          discountCents = 0;
          discountLabel = "";
        }
      }

      discountCents = clamp(
        discountCents,
        0,
        originalTotal
      );

      const finalTotal =
        originalTotal -
        discountCents;

      const finalEach =
        Math.round(
          finalTotal / quantity
        );

      return {
        quantity,
        unitPrice,
        originalTotal,
        finalTotal,
        finalEach,
        discountCents,
        discountLabel
      };
    }

    /* =======================================================
       UPDATE PURCHASE OPTION PRICE
       ======================================================= */

    updateQuantityPrice(
      quantityOption
    ) {
      if (!this.selectedSize) {
        return;
      }

      const pricing =
        this.calculatePricing(
          quantityOption
        );

      const totalElement =
        quantityOption.querySelector(
          SELECTORS.totalPrice
        );

      const compareElement =
        quantityOption.querySelector(
          SELECTORS.comparePrice
        );

      const eachElement =
        quantityOption.querySelector(
          SELECTORS.eachPrice
        );

      const discountElement =
        quantityOption.querySelector(
          SELECTORS.discountText
        );

      if (!pricing) {
        if (totalElement) {
          totalElement.textContent = "—";
        }

        if (eachElement) {
          eachElement.textContent = "—";
        }

        if (compareElement) {
          compareElement.textContent = "";
          compareElement.hidden = true;
        }

        if (discountElement) {
          discountElement.textContent = "";
          discountElement.hidden = true;
        }

        return;
      }

      if (totalElement) {
        totalElement.textContent =
          formatMoney(
            pricing.finalTotal
          );
      }

      if (eachElement) {
        eachElement.textContent =
          `${formatMoney(
            pricing.finalEach
          )} each`;
      }

      if (compareElement) {
        if (
          pricing.discountCents > 0
        ) {
          compareElement.textContent =
            formatMoney(
              pricing.originalTotal
            );

          compareElement.hidden = false;
        } else {
          compareElement.textContent = "";
          compareElement.hidden = true;
        }
      }

      if (discountElement) {
        if (
          pricing.discountCents > 0 &&
          pricing.discountLabel
        ) {
          discountElement.textContent =
            pricing.discountLabel;

          discountElement.hidden = false;
        } else {
          discountElement.textContent = "";
          discountElement.hidden = true;
        }
      }
    }

    /* =======================================================
       CLEAR CALCULATED PRICES
       ======================================================= */

    clearCalculatedPrices() {
      this.quantityOptions.forEach(
        (option) => {
          const totalElement =
            option.querySelector(
              SELECTORS.totalPrice
            );

          const compareElement =
            option.querySelector(
              SELECTORS.comparePrice
            );

          const eachElement =
            option.querySelector(
              SELECTORS.eachPrice
            );

          const discountElement =
            option.querySelector(
              SELECTORS.discountText
            );

          if (totalElement) {
            totalElement.textContent = "—";
          }

          if (eachElement) {
            eachElement.textContent = "—";
          }

          if (compareElement) {
            compareElement.textContent = "";
            compareElement.hidden = true;
          }

          if (discountElement) {
            discountElement.textContent = "";
            discountElement.hidden = true;
          }
        }
      );
    }

    /* =======================================================
       PURCHASE OPTION STEPPER
       ======================================================= */

    updateCounter() {
      if (
        !this.selectedQuantity ||
        this.visibleQuantityOptions.length === 0
      ) {
        if (this.counterValue) {
          this.counterValue.textContent =
            "0";
        }

        if (this.decreaseButton) {
          this.decreaseButton.disabled =
            true;
        }

        if (this.increaseButton) {
          this.increaseButton.disabled =
            true;
        }

        return;
      }

      const selectedIndex =
        this.visibleQuantityOptions.indexOf(
          this.selectedQuantity
        );

      const selectedValue =
        parseInteger(
          this.selectedQuantity.dataset
            .quantity,
          1
        );

      if (this.counterValue) {
        this.counterValue.textContent =
          String(selectedValue);
      }

      if (this.decreaseButton) {
        this.decreaseButton.disabled =
          selectedIndex <= 0;
      }

      if (this.increaseButton) {
        this.increaseButton.disabled =
          selectedIndex >=
          this.visibleQuantityOptions.length - 1;
      }
    }

    /* =======================================================
       ADD BUTTON STATE
       ======================================================= */

    updateAddButtonState() {
      if (!this.addButton) {
        return;
      }

      const variantId =
        parseInteger(
          this.selectedSize?.dataset
            .variantId,
          0
        );

      const shopifyAvailable =
        parseBoolean(
          this.selectedSize?.dataset
            .available
        );

      const priceReady =
        parseBoolean(
          this.selectedSize?.dataset
            .shopifyPriceReady
        );

      const quantityIsValid =
        this.selectedQuantity &&
        this.visibleQuantityOptions.includes(
          this.selectedQuantity
        );

      this.addButton.disabled =
        this.isAdding ||
        !this.selectedSize ||
        !quantityIsValid ||
        !variantId ||
        !shopifyAvailable ||
        !priceReady;
    }

    disableAddButton() {
      if (this.addButton) {
        this.addButton.disabled = true;
      }
    }

    setLoading(loading) {
      this.isAdding = loading;

      this.addButton?.classList.toggle(
        "is-loading",
        loading
      );

      this.updateAddButtonState();
    }

    /* =======================================================
       MESSAGES
       ======================================================= */

    clearMessage() {
      if (!this.message) {
        return;
      }

      this.message.textContent = "";

      this.message.className =
        "bnx-product-builder__message";
    }

    showMessage(message, type) {
      if (!this.message) {
        return;
      }

      this.message.textContent = message;

      this.message.className =
        `bnx-product-builder__message is-${type}`;
    }

    /* =======================================================
       SHOPIFY NATIVE ADD TO CART
       ======================================================= */

    async handleAddToCart(event) {
      event.preventDefault();

      if (
        this.isAdding ||
        !this.selectedSize ||
        !this.selectedQuantity ||
        !this.visibleQuantityOptions.includes(
          this.selectedQuantity
        )
      ) {
        return;
      }

      const variantId =
        parseInteger(
          this.selectedSize.dataset
            .variantId,
          0
        );

      const quantity =
        Math.max(
          1,
          parseInteger(
            this.selectedQuantity.dataset
              .quantity,
            1
          )
        );

      const sizeName =
        this.selectedSize.dataset
          .sizeName ||
        "product";

      const optionLabel =
        this.selectedQuantity.dataset
          .optionLabel ||
        this.selectedQuantity.dataset
          .label ||
        "";

      const shopifyAvailable =
        parseBoolean(
          this.selectedSize.dataset
            .available
        );

      if (!variantId) {
        this.showMessage(
          "The selected Shopify variant is unavailable.",
          "error"
        );

        return;
      }

      if (!shopifyAvailable) {
        this.showMessage(
          "The selected product is currently unavailable.",
          "error"
        );

        return;
      }

      this.setLoading(true);
      this.clearMessage();

      try {
        const root =
          window.Shopify?.routes?.root ||
          "/";

        /*
         * IMPORTANT:
         *
         * This remains Shopify's native cart.
         * No Bottle Nexus checkout dependency.
         */

        const response =
          await fetch(
            `${root}cart/add.js`,
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json",

                Accept:
                  "application/json"
              },

              body: JSON.stringify({
                items: [
                  {
                    id: variantId,
                    quantity
                  }
                ]
              })
            }
          );

        const result =
          await response.json();

        if (!response.ok) {
          throw new Error(
            result?.description ||
              result?.message ||
              "Unable to add this product to the cart."
          );
        }

        const successLabel =
          optionLabel ||
          `${quantity} × ${sizeName}`;

        this.showMessage(
          `${successLabel} added to your cart.`,
          "success"
        );

        window.dispatchEvent(
          new CustomEvent(
            "bnx-product-builder:added",
            {
              detail: {
                variantId,

                quantity,

                sizeName,

                optionLabel,

                priceCents:
                  parseInteger(
                    this.selectedSize
                      .dataset.priceCents,
                    0
                  ),

                shopifyProductId:
                  this.selectedSize.dataset
                    .productId,

                productHandle:
                  this.selectedSize.dataset
                    .productHandle
              }
            }
          )
        );

        document.body.classList.add(
          "cart-drawer-show"
        );

        if (
          typeof window.sectionRendering ===
          "function"
        ) {
          window.sectionRendering();
        }
      } catch (error) {
        console.error(
          "Shopify add-to-cart error:",
          error
        );

        this.showMessage(
          error?.message ||
            "Unable to add this product. Please try again.",
          "error"
        );
      } finally {
        this.setLoading(false);
      }
    }
  }

  /* =========================================================
     INITIALIZER
     ========================================================= */

  function initializeBuilder(element) {
    if (
      !element ||
      initializedBuilders.has(element)
    ) {
      return;
    }

    initializedBuilders.add(element);

    const builder =
      new ShopifyProductBuilder(element);

    builder.init();
  }

  function initializeAllBuilders(
    scope = document
  ) {
    if (
      scope.matches?.(
        SELECTORS.builder
      )
    ) {
      initializeBuilder(scope);
    }

    scope
      .querySelectorAll?.(
        SELECTORS.builder
      )
      .forEach(
        initializeBuilder
      );
  }

  /* =========================================================
     PAGE LOAD
     ========================================================= */

  if (
    document.readyState === "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      () =>
        initializeAllBuilders(),
      {
        once: true
      }
    );
  } else {
    initializeAllBuilders();
  }

  /* =========================================================
     SHOPIFY THEME EDITOR
     ========================================================= */

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeAllBuilders(
        event.target
      );
    }
  );
})();